class WsOrders {
  private socket: WebSocket | null = null;
  private url: string;
  private readonly listeners: Set<(data: any) => void> = new Set();

  private reconnectTimeout: number | null = null;
  private shouldReconnect = true;

  private generation = 0;

  constructor(sessToken: string, botId: number | string, cycleId: number) {
      this.url = `wss://ws.revenuebot.io/AuthWsOrders?sess=${encodeURIComponent(
          sessToken ?? '',
      )}&bot_id=${encodeURIComponent(String(botId))}&cycle_id=${encodeURIComponent(
          String(cycleId),
      )}`;
  }

  connect(): void {
      if (
          this.socket &&
      (this.socket.readyState === WebSocket.CONNECTING ||
        this.socket.readyState === WebSocket.OPEN)
      ) {
          console.warn('WebSocket already connecting/open.');
          return;
      }

      this.shouldReconnect = true;

      this.generation += 1;
      const gen = this.generation;

      if (this.reconnectTimeout !== null) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
      }

      const socket = new WebSocket(this.url);
      this.socket = socket;

      socket.onopen = () => {
          if (gen !== this.generation) return;

          if (this.reconnectTimeout !== null) {
              clearTimeout(this.reconnectTimeout);
              this.reconnectTimeout = null;
          }
      };

      socket.onmessage = (event) => {
          if (gen !== this.generation) return;

          try {
              const data = JSON.parse(event.data);
              this.listeners.forEach((callback) => callback(data));
          } catch (e) {
              console.error('WebSocket message JSON parse error:', e, event.data);
          }
      };

      socket.onclose = (event) => {
          const rs = socket.readyState;

          console.warn('WS closed', {
              code: event.code,
              reason: event.reason,
              wasClean: event.wasClean,
              url: this.url,
              readyState: rs,
              closedWhileConnecting: rs === WebSocket.CONNECTING,
          });

          if (gen !== this.generation) return;

          if (this.socket === socket) {
              this.socket = null;
          }

          if (this.shouldReconnect) {
              this.reconnect(gen);
          }
      };

      socket.onerror = (error) => {
          if (gen !== this.generation) return;
          console.error('WebSocket error:', error);
      };
  }

  disconnect(): void {
      this.shouldReconnect = false;
      this.generation += 1;

      if (this.reconnectTimeout !== null) {
          clearTimeout(this.reconnectTimeout);
          this.reconnectTimeout = null;
      }

      if (this.socket) {
          this.socket.close(1000, 'client disconnect');
          this.socket = null;
      }
  }

  private reconnect(genAtSchedule: number): void {
      if (this.reconnectTimeout !== null || !this.shouldReconnect) return;

      console.log('Attempting to reconnect in 5 seconds...');
      this.reconnectTimeout = window.setTimeout(() => {
          this.reconnectTimeout = null;

          if (!this.shouldReconnect || this.generation !== genAtSchedule) return;

          this.connect();
      }, 5000);
  }

  sendMessage(message: any): void {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(JSON.stringify(message));
      } else {
          console.warn('WebSocket is not connected.');
      }
  }

  isConnected(): boolean {
      return this.socket !== null && this.socket.readyState === WebSocket.OPEN;
  }

  isConnecting(): boolean {
      return this.socket !== null && this.socket.readyState === WebSocket.CONNECTING;
  }

  getReadyState(): number | null {
      return this.socket ? this.socket.readyState : null;
  }

  addListener(callback: (data: any) => void): void {
      this.listeners.add(callback);
  }

  removeListener(callback: (data: any) => void): void {
      this.listeners.delete(callback);
  }

  clearListeners(): void {
      this.listeners.clear();
  }
}

export default WsOrders;